// Phase 3A API helpers
import { api } from "./api";

export const pricingApi = {
  list: (tourId) => api.get(`/tours/${tourId}/pricing`).then((r) => r.data),
  summary: (tourId) => api.get(`/tours/${tourId}/pricing/summary`).then((r) => r.data),
  add: (tourId, body) => api.post(`/tours/${tourId}/pricing`, body).then((r) => r.data),
  patch: (lineId, body) => api.patch(`/pricing/${lineId}`, body).then((r) => r.data),
  remove: (lineId) => api.delete(`/pricing/${lineId}`).then((r) => r.data),
};

export const quoteApi = {
  list: (tourId) => api.get(`/tours/${tourId}/quotes`).then((r) => r.data),
  create: (tourId, body = {}) => api.post(`/tours/${tourId}/quotes`, body).then((r) => r.data),
  patch: (id, body) => api.patch(`/quotes/${id}`, body).then((r) => r.data),
  setStatus: (id, status, reason) =>
    api.post(`/quotes/${id}/status`, { status, reason }).then((r) => r.data),
  discardDraft: (id) => api.delete(`/quotes/${id}`).then((r) => r.data),
};

export const contractApi = {
  templates: () => api.get("/contract-templates").then((r) => r.data),
  list: (tourId) => api.get(`/tours/${tourId}/contracts`).then((r) => r.data),
  create: (tourId, body) => api.post(`/tours/${tourId}/contracts`, body).then((r) => r.data),
  patch: (id, body) => api.patch(`/contracts/${id}`, body).then((r) => r.data),
  setStatus: (id, status, reason) =>
    api.post(`/contracts/${id}/status`, { status, reason }).then((r) => r.data),
  discardDraft: (id) => api.delete(`/contracts/${id}`).then((r) => r.data),
  addPaymentRow: (id, body) =>
    api.post(`/contracts/${id}/payment-schedule`, body).then((r) => r.data),
  editPaymentRow: (id, rowId, body) =>
    api.patch(`/contracts/${id}/payment-schedule/${rowId}`, body).then((r) => r.data),
  removePaymentRow: (id, rowId) =>
    api.delete(`/contracts/${id}/payment-schedule/${rowId}`).then((r) => r.data),
  markPaid: (id, rowId, paidAmount) =>
    api
      .post(`/contracts/${id}/payment-schedule/${rowId}/mark-paid`, { paid_amount: paidAmount })
      .then((r) => r.data),
};

export const pickupSheetApi = {
  list: (tourId) => api.get(`/tours/${tourId}/pickup-sheets`).then((r) => r.data),
  generate: (tourId, driverAssignmentId) =>
    api.post(`/tours/${tourId}/pickup-sheets`, { driver_assignment_id: driverAssignmentId }).then((r) => r.data),
  mine: () => api.get("/pickup-sheets/mine").then((r) => r.data),
  // Canonical Pickup Sheet workflow (Phases 3-6)
  readiness: (tourId, daId) =>
    api.get(`/tours/${tourId}/pickup-sheets/${daId}/readiness`).then((r) => r.data),
  workflow: (tourId, daId) =>
    api.get(`/tours/${tourId}/pickup-sheets/${daId}/workflow`).then((r) => r.data),
  previewUrl: (tourId, daId) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/tours/${tourId}/pickup-sheets/${daId}/preview`,
  officeApprove: (tourId, daId) =>
    api.post(`/tours/${tourId}/pickup-sheets/${daId}/office-approve`).then((r) => r.data),
  retryFinalPdf: (tourId, daId) =>
    api.post(`/tours/${tourId}/pickup-sheets/${daId}/retry-final-pdf`).then((r) => r.data),
  logPreview: (tourId, daId) =>
    api.post(`/tours/${tourId}/pickup-sheets/${daId}/log-preview`).then((r) => r.data).catch(() => {}),
  markPrintedProvided: (tourId, daId) =>
    api.post(`/tours/${tourId}/pickup-sheets/${daId}/mark-printed-provided`).then((r) => r.data),
};

export const departureApi = {
  get: (tourId) => api.get(`/tours/${tourId}/departure-checklist`).then((r) => r.data),
  patchItem: (tourId, itemId, body) =>
    api.patch(`/tours/${tourId}/departure-checklist/items/${itemId}`, body).then((r) => r.data),
  readiness: (tourId) => api.get(`/tours/${tourId}/departure-checklist/readiness`).then((r) => r.data),
};

export const dashboardApi = {
  // Legacy pre-Wave-1 fixed-tile aggregator (moved server-side to
  // /dashboard/legacy-summary). No live consumers post-Phase-C.
  legacySummary: () => api.get("/dashboard/legacy-summary").then((r) => r.data),
  // Phase C Wave 1 — universal widget summary. Pass a list of widget keys;
  // server returns { widgets: { key: { data, status, is_placeholder?, unavailable_reason? } } }.
  widgetSummary: (keys) =>
    api
      .get("/dashboard/summary", { params: { widgets: (keys || []).join(",") } })
      .then((r) => r.data),
  // Resolved dashboard_config for the current user (user override or role
  // class default) + perspective_label + role_default_widgets.
  me: () => api.get("/dashboard/me").then((r) => r.data),
  // Effective permission set (used to filter the Add-Widget picker).
  myPermissions: () => api.get("/dashboard/permissions/me").then((r) => r.data),
  // Static widget catalog (server-authoritative mirror of dashboardWidgetCatalog.js).
  widgets: () => api.get("/dashboard/widgets").then((r) => r.data),
  // Phase C Wave 2A.1 — layout persistence contract (feature-flagged
  // NO-OP server-side). Client receives {persisted:false, layout, message}
  // and treats the save as in-session only.
  saveLayout: (layout) =>
    api.put("/dashboard/me/layout", layout).then((r) => r.data),
  restoreRoleDefault: () =>
    api.post("/dashboard/me/layout/restore-default").then((r) => r.data),
};

export const absoluteFileUrl = (relativeApiPath) => {
  const base = process.env.REACT_APP_BACKEND_URL;
  return `${base}${relativeApiPath}`;
};
